import { IContainer } from "@aurelia/kernel";
import { Scope, getCollectionObserver, BindingContext, } from '@aurelia/runtime';
import { customAttribute, IInstruction, IController, IViewFactory, IRenderLocation, IPlatform, } from '@aurelia/runtime-html';
import { unwrapExpression, } from "./repeat-utilities";
import { ICollectionStrategyLocator, IDomRenderer, IScrollerObsererLocator, } from "./interfaces";
import { calcOuterHeight, getDistanceToScroller } from "./utilities-dom";
export class VirtualRepeat {
    constructor(location, instruction, parent, factory, container, platform) {
        var _a;
        this.location = location;
        this.instruction = instruction;
        this.parent = parent;
        this.factory = factory;
        this.container = container;
        /**@internal */
        this.views = [];
        this.itemHeight = 0;
        this.minViewsRequired = 0;
        const iteratorInstruction = instruction.instructions[0];
        const forOf = iteratorInstruction.from;
        const iterable = this.iterable = (_a = unwrapExpression(forOf.iterable)) !== null && _a !== void 0 ? _a : forOf.iterable;
        const hasWrapExpression = this.hasWrapExpression = forOf.iterable !== iterable;
        this.obsMediator = new CollectionObservationMediator(this, hasWrapExpression ? 'handleInnerCollectionChange' : 'handleCollectionChange');
        this.local = forOf.declaration.name;
        this.taskQueue = platform.domWriteQueue;
    }
    static get inject() {
        return [IRenderLocation, IInstruction, IController, IViewFactory, IContainer, IPlatform];
    }
    /**
     * @internal
     */
    attaching() {
        const container = this.container;
        const collectionStrategyLocator = container.get(ICollectionStrategyLocator);
        const collectionStrategy = this.collectionStrategy = collectionStrategyLocator.getStrategy(this.items);
        const itemCount = collectionStrategy.count();
        if (itemCount === 0) {
            // just wait?
            return;
        }
        const repeatDom = this.dom = container.get(IDomRenderer).render(this.location);
        const firstView = this.createAndActivateFirstView();
        (this.scrollerObserver = container.get(IScrollerObsererLocator).getObserver(repeatDom.getScroller()))
            .subscribe(this);
        this.initCalculation(firstView);
        this.itemsChanged(this.items);
    }
    /**
     * @internal
     */
    detaching() {
        var _a;
        (_a = this.task) === null || _a === void 0 ? void 0 : _a.cancel();
        this.resetCalculation();
        this.dom.dispose();
        this.scrollerObserver.unsubscribe(this);
        this.dom
            = this.scrollerObserver
                = this.task
                    = void 0;
    }
    /**
     * @internal
     */
    initCalculation(firstView) {
        const itemHeight = calcOuterHeight(firstView.nodes.firstChild);
        const scrollerInfo = this.scrollerObserver.getValue();
        const calculation = this.calculate(scrollerInfo, this.collectionStrategy.count(), itemHeight);
        if (calculation.signals & 1 /* reset */) {
            this.resetCalculation();
        }
        if ((calculation.signals & 2 /* has_sizing */) === 0) {
            // when sizing calculation fails
            // dirty check?
            return;
        }
        this.itemHeight = itemHeight;
        this.minViewsRequired = calculation.minViews;
    }
    /**
     * @internal
     */
    calculate(scrollerInfo, itemCount, itemHeight) {
        if (itemCount === 0) {
            return Calculation.reset;
        }
        if (itemHeight === 0) {
            return Calculation.none;
        }
        const minViewsRequired = Math.floor(scrollerInfo.height / itemHeight);
        return Calculation.from(2 /* has_sizing */, minViewsRequired);
    }
    /**
     * @internal
     */
    resetCalculation() {
        this.minViewsRequired = 0;
        this.itemHeight = 0;
    }
    /**@internal */
    itemsChanged(items) {
        const controller = this.$controller;
        const collectionStrategy = this.collectionStrategy = this.container.get(ICollectionStrategyLocator).getStrategy(items);
        const itemCount = collectionStrategy.count();
        const views = this.views;
        const maxViewsRequired = this.minViewsRequired * 2;
        let i = 0;
        let currViewCount = views.length;
        let view = null;
        if (itemCount === 0) {
            // todo: no async supported
            for (i = 0; currViewCount > i; ++i) {
                view = views[i];
                view.deactivate(controller, controller, 4 /* fromUnbind */);
                view.nodes.remove();
            }
            views.length = 0;
            this.resetCalculation();
            return;
        }
        if (currViewCount > maxViewsRequired) {
            while (currViewCount > maxViewsRequired) {
                view = views[currViewCount - 1];
                view.deactivate(controller, controller, 4 /* fromUnbind */);
                view.nodes.remove();
                --currViewCount;
            }
            views.length = currViewCount;
        }
        if (currViewCount > itemCount) {
            // remove views from bottom to top
            while (currViewCount > itemCount) {
                view = views[currViewCount - 1];
                view.deactivate(controller, controller, 4 /* fromUnbind */);
                view.nodes.remove();
                --currViewCount;
            }
            views.length = itemCount;
        }
        currViewCount = views.length;
        // only ensure there's enough views
        // don't activate yet
        const realViewCount = Math.min(maxViewsRequired, itemCount);
        for (i = currViewCount; i < realViewCount; i++) {
            views.push(this.factory.create());
        }
        const itemHeight = this.itemHeight;
        const local = this.local;
        const { firstIndex, topCount, botCount, } = this.measureBuffer(this.scrollerObserver.getValue(), views.length, itemCount, itemHeight);
        let idx = 0;
        let item;
        let prevView;
        let scope;
        for (i = 0; realViewCount > i; ++i) {
            idx = firstIndex + i;
            item = collectionStrategy.item(idx);
            view = views[i];
            prevView = views[i - 1];
            if (view.isActive) {
                scope = view.scope;
                scope.bindingContext[local] = item;
                scope.overrideContext.$index = idx;
                scope.overrideContext.$length = itemCount;
            }
            else {
                view.nodes.insertBefore(prevView.nodes.firstChild.nextSibling);
                scope = Scope.fromParent(controller.scope, BindingContext.create(local, collectionStrategy.item(idx)));
                scope.overrideContext.$index = idx;
                scope.overrideContext.$length = itemCount;
                enhanceOverrideContext(scope.overrideContext);
                view.activate(controller, controller, 2 /* fromBind */, scope, controller.hostScope);
            }
        }
        this.obsMediator.start(items);
        this.dom.update(topCount * itemHeight, botCount * itemHeight);
    }
    /**
     * The value returned by HTMLElement.prototype.scrollTop isn't always reliable.
     * When the virtual repeater is placed after a long list of elements, its "real" scrolltop
     * will be different with this value. An example is virtual repeat on table,
     * the header shouldn't be of the scroll top calculation
     *
     * @internal
     */
    calcRealScrollTop(scrollerInfo) {
        const scroller_scroll_top = scrollerInfo.scrollTop;
        const top_buffer_distance = getDistanceToScroller(this.dom.top, scrollerInfo.scroller);
        const real_scroll_top = Math.max(0, scroller_scroll_top === 0
            ? 0
            : (scroller_scroll_top - top_buffer_distance));
        return real_scroll_top;
    }
    /**@internal */
    measureBuffer(scrollerInfo, viewCount, collectionSize, itemHeight) {
        const real_scroll_top = this.calcRealScrollTop(scrollerInfo);
        let first_index_after_scroll_adjustment = real_scroll_top === 0
            ? 0
            : Math.floor(real_scroll_top / itemHeight);
        // if first index after scroll adjustment doesn't fit with number of possible view
        // it means the scroller has been too far down to the bottom and nolonger suitable to start from this index
        // rollback until all views fit into new collection, or until has enough collection item to render
        if (first_index_after_scroll_adjustment + viewCount >= collectionSize) {
            first_index_after_scroll_adjustment = Math.max(0, collectionSize - viewCount);
        }
        const top_buffer_item_count_after_scroll_adjustment = first_index_after_scroll_adjustment;
        const bot_buffer_item_count_after_scroll_adjustment = Math.max(0, collectionSize - top_buffer_item_count_after_scroll_adjustment - viewCount);
        return {
            firstIndex: first_index_after_scroll_adjustment,
            topCount: top_buffer_item_count_after_scroll_adjustment,
            botCount: bot_buffer_item_count_after_scroll_adjustment,
        };
    }
    handleScrollerChange(scrollerInfo) {
        const task = this.task;
        this.task = this.taskQueue.queueTask(() => {
            this.task = null;
            this.handleScroll(scrollerInfo);
        });
        task === null || task === void 0 ? void 0 : task.cancel();
    }
    /**@internal */
    handleScroll(scrollerInfo) {
        if (this.itemHeight === 0) {
            return;
        }
        const local = this.local;
        const itemHeight = this.itemHeight;
        const repeatDom = this.dom;
        const views = this.views;
        const collectionStrategy = this.collectionStrategy;
        const viewCount = views.length;
        const collectionSize = collectionStrategy.count();
        const prevFirstIndex = views[0].scope.overrideContext.$index;
        const { firstIndex: currFirstIndex, topCount: topCount1, botCount: botCount1 } = this.measureBuffer(scrollerInfo, viewCount, collectionSize, itemHeight);
        const isScrollingDown = currFirstIndex > prevFirstIndex;
        const isJumping = isScrollingDown
            ? currFirstIndex >= prevFirstIndex + viewCount
            : currFirstIndex + viewCount <= prevFirstIndex;
        if (currFirstIndex === prevFirstIndex) {
            // console.log('scrolling, but not scrolling');
            // not moving enough to change the view range
            // so just check get more or not
            if ( /* is scrolling up & near top */true) {
            }
            if ( /* is scrolling down & near bottom */true) {
            }
            // exit here
            return;
        }
        let view = null;
        let scope = null;
        let idx = 0;
        let viewsToMoveCount = 0;
        let idxIncrement = 0;
        let i = 0;
        if (isJumping) {
            for (i = 0; viewCount > i; ++i) {
                idx = currFirstIndex + i;
                scope = views[i].scope;
                scope.bindingContext[local] = collectionStrategy.item(idx);
                scope.overrideContext.$index = idx;
                scope.overrideContext.$length = collectionSize;
            }
        }
        else if (isScrollingDown) {
            viewsToMoveCount = currFirstIndex - prevFirstIndex;
            while (viewsToMoveCount > 0) {
                view = views.shift();
                idx = views[views.length - 1].scope.overrideContext['$index'] + 1;
                views.push(view);
                scope = view.scope;
                scope.bindingContext[local] = collectionStrategy.item(idx);
                scope.overrideContext.$index = idx;
                scope.overrideContext.$length = collectionSize;
                view.nodes.insertBefore(repeatDom.bottom);
                ++idxIncrement;
                --viewsToMoveCount;
            }
        }
        else {
            viewsToMoveCount = prevFirstIndex - currFirstIndex;
            while (viewsToMoveCount > 0) {
                idx = prevFirstIndex - (idxIncrement + 1);
                view = views.pop();
                scope = view.scope;
                scope.bindingContext[local] = collectionStrategy.item(idx);
                scope.overrideContext.$index = idx;
                scope.overrideContext.$length = collectionSize;
                view.nodes.insertBefore(views[0].nodes.firstChild);
                views.unshift(view);
                ++idxIncrement;
                --viewsToMoveCount;
            }
        }
        if (isScrollingDown) {
            if (collectionStrategy.isNearBottom(currFirstIndex + (viewCount - 1))) {
                // console.log('getting more item when scrolling down');
            }
        }
        else {
            if (collectionStrategy.isNearTop(views[0].scope.overrideContext['$index'])) {
                // console.log('getting more item when scrolling up');
            }
        }
        repeatDom.update(topCount1 * itemHeight, botCount1 * itemHeight);
    }
    /**
     * @internal
     */
    handleCollectionChange(_indexMap, _flags) {
        this.itemsChanged(this.items);
        // const [start, end] = this.range;
        // const itemHeight = this.itemHeight;
        // const collectionSize = this.collectionStrategy.count();
        // let topBuffer = start;
        // let botBuffer = Math.max(
        //   0,
        //   collectionSize - end
        // )
        // if (this.collectionStrategy.count() > 0) {
        //   const deletion = indexMap.deletedItems;
        //   const hasDeletion = deletion.length > 0;
        //   if (hasDeletion) {
        //     const deletionIndex = deletion[0];
        //     const deletionCount = deletion.length;
        //     if (deletionIndex > end) {
        //       botBuffer = Math.max(0, (collectionSize - botBuffer) - deletionCount);
        //     } else if (deletionIndex < start && (deletionIndex + (deletionCount - 1)) < start) {
        //       topBuffer = Math.max(0, topBuffer - (deletionCount - 1));
        //     } else {
        //       if (deletionIndex + deletionCount >= collectionSize) {
        //       }
        //     }
        //   } else {
        //   }
        // }
        // this.dom.update(topBuffer * itemHeight, botBuffer * itemHeight);
    }
    /**
     * @internal
     */
    handleInnerCollectionChange() {
        const newItems = this.iterable.evaluate(0 /* none */, this.parent.scope, this.parent.hostScope, this.container, null);
        const oldItems = this.items;
        this.items = newItems;
        if (newItems === oldItems) {
            this.itemsChanged(newItems);
        }
    }
    /**
     * @internal
     */
    createAndActivateFirstView() {
        const firstView = this.getOrCreateFirstView();
        const repeatController = this.$controller;
        const collectionStrategy = this.collectionStrategy;
        const parentScope = repeatController.scope;
        const hostScope = repeatController.hostScope;
        const itemScope = Scope.fromParent(parentScope, BindingContext.create(this.local, collectionStrategy.first()));
        itemScope.overrideContext.$index = 0;
        itemScope.overrideContext.$length = collectionStrategy.count();
        enhanceOverrideContext(itemScope.overrideContext);
        firstView.nodes.insertBefore(this.dom.bottom);
        // todo: maybe state upfront that async lifecycle aren't supported with virtual-repeat
        firstView.activate(repeatController, repeatController, 0 /* none */, itemScope, hostScope);
        return firstView;
    }
    /**
     * @internal
     */
    getOrCreateFirstView() {
        const views = this.views;
        if (views.length > 0) {
            return views[0];
        }
        const view = this.factory.create();
        views.push(view);
        return view;
    }
}
// avoid excessive code generation, if it doesn't affect readability too much
customAttribute({
    isTemplateController: true,
    name: 'virtual-repeat',
    bindables: {
        local: { property: 'local' },
        items: { property: 'items', primary: true }
    }
})(VirtualRepeat);
class CollectionObservationMediator {
    constructor(repeat, key) {
        this.repeat = repeat;
        this.key = key;
    }
    handleCollectionChange(indexMap, flags) {
        this.repeat[this.key](indexMap, flags);
    }
    start(c) {
        var _a;
        if (this.c === c) {
            return;
        }
        this.stop();
        (_a = getCollectionObserver(this.c = c)) === null || _a === void 0 ? void 0 : _a.subscribe(this);
    }
    stop() {
        var _a;
        (_a = getCollectionObserver(this.c)) === null || _a === void 0 ? void 0 : _a.unsubscribe(this);
    }
}
class Calculation {
    constructor(signals, minViews) {
        this.signals = signals;
        this.minViews = minViews;
    }
    static from(signals, minViews) {
        return new Calculation(signals, minViews);
    }
}
Calculation.reset = new Calculation(1 /* reset */, 0);
Calculation.none = new Calculation(0 /* none */, 0);
const enhancedContextCached = new WeakSet();
function enhanceOverrideContext(context) {
    const ctx = context;
    if (enhancedContextCached.has(ctx)) {
        return;
    }
    Object.defineProperties(ctx, {
        $first: createGetterDescriptor($first),
        $last: createGetterDescriptor($last),
        $middle: createGetterDescriptor($middle),
        $even: createGetterDescriptor($even),
        $odd: createGetterDescriptor($odd),
    });
}
function createGetterDescriptor(getter) {
    return { configurable: true, enumerable: true, get: getter };
}
function $even() {
    return this.$index % 2 === 0;
}
function $odd() {
    return this.$index % 2 !== 0;
}
function $first() {
    return this.$index === 0;
}
function $last() {
    return this.$index === this.$length - 1;
}
function $middle() {
    return this.$index > 0 && this.$index < (this.$length - 1);
}
